import {
	createContext,
	useContext,
	useState,
	useCallback,
	ReactNode,
	useEffect,
	useMemo,
} from 'react';

// Global debug context instance for direct access
let globalDebugInstance: DebugContextType | null = null;

// Extend window type for debug context
declare global {
	interface Window {
		__DEBUG_CONTEXT__?: DebugContextType;
	}
}

export interface APICallLog {
	id: string;
	timestamp: Date;
	endpoint: string;
	method: string;
	payload?: unknown;
	status: number;
	duration: number;
	response?: unknown;
	error?: unknown;
	isError: boolean;
}

export interface DebugContextType {
	isDebugMode: boolean;
	apiLogs: APICallLog[];
	addApiLog: (call: Omit<APICallLog, 'id' | 'timestamp'>) => void;
	clearApiLogs: () => void;
	setDebugMode: (mode: boolean) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

export const useDebug = () => {
	const context = useContext(DebugContext);
	if (!context) {
		throw new Error('useDebug must be used within a DebugProvider');
	}
	return context;
};

// Direct function to add API logs (for use in fetcher)
export const addDebugApiLog = (call: Omit<APICallLog, 'id' | 'timestamp'>) => {
	if (globalDebugInstance) {
		globalDebugInstance.addApiLog(call);
	}
};

interface DebugProviderProps {
	children: ReactNode;
}

export const DebugProvider: React.FC<DebugProviderProps> = ({ children }) => {
	const [isDebugMode, setIsDebugMode] = useState(false);
	const [apiLogs, setApiLogs] = useState<APICallLog[]>([]);

	const addApiLog = useCallback(
		(call: Omit<APICallLog, 'id' | 'timestamp'>) => {
			const newLog: APICallLog = {
				...call,
				id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
				timestamp: new Date(),
			};

			setApiLogs((prev) => [newLog, ...prev].slice(0, 100));
		},
		[],
	);

	const clearApiLogs = useCallback(() => {
		setApiLogs([]);
	}, []);

	const setDebugMode = useCallback(
		(mode: boolean) => {
			setIsDebugMode(mode);
			if (!mode) {
				clearApiLogs();
			}
		},
		[clearApiLogs],
	);

	const value: DebugContextType = useMemo(
		() => ({
			isDebugMode,
			apiLogs,
			addApiLog,
			clearApiLogs,
			setDebugMode,
		}),
		[isDebugMode, apiLogs, addApiLog, clearApiLogs, setDebugMode],
	);

	// Update global instance and expose to window
	useEffect(() => {
		globalDebugInstance = value;
		if (typeof window !== 'undefined') {
			window.__DEBUG_CONTEXT__ = value;
		}
	}, [value]);

	return (
		<DebugContext.Provider value={value}>{children}</DebugContext.Provider>
	);
};
